(function () {
	'use strict';
	// ===========================================================================
	// Cross-storefront cart sync — Liquid theme  <->  Hydrogen storefront
	// ===========================================================================
	//
	// WHY THIS EXISTS
	// ---------------
	// NativePath runs two front ends on two subdomains of the SAME site:
	//     • this Liquid theme   ->  shop.nativepath.com
	//     • the Hydrogen app    ->  nativepath.com   (repo: nativepath-v2)
	// Both are backed by the same Shopify store, so a cart token created on one is
	// valid on the other. But Shopify scopes its native `cart` cookie to the exact
	// host that set it (host-only), so it never crosses between the two subdomains
	// and a shopper's cart appears empty after crossing over.
	//
	// The fix is the same cross-subdomain cookie technique used for attribution in
	// `assets/landing-params.js`: purpose-built cookies scoped `Domain=.nativepath.com`.
	//
	// THIS FILE HANDLES BOTH DIRECTIONS (one file on purpose — they share cookie
	// helpers and the host guard, and REVERSE must run before FORWARD, which is
	// only guaranteed when they live in the same script):
	//
	//   REVERSE  Hydrogen -> theme   cookie `np_cart_hydrogen`
	//     Hydrogen (nativepath-v2/server.ts) writes its cart token here. We adopt
	//     it by OVERWRITING the theme's native `cart` cookie (the Online Store
	//     loads whatever valid token is in that cookie — verified behavior). No
	//     reload: setting the cookie fixes every future render, and we patch the
	//     CURRENT page's visible cart surfaces in place via the Section Rendering
	//     API (renders in the swapped cart's context — verified).
	//     Policy: "Hydrogen wins" (when non-empty), consume-on-adopt.
	//       - Overwrites even a NON-empty theme cart when Hydrogen has items.
	//       - BUT an EMPTY Hydrogen cart never wipes a theme cart: we verify the
	//         adopted cart is non-empty (via /cart.js) and restore the theme cart
	//         if it isn't.
	//       - DELETES `np_cart_hydrogen` after adopting, so it wins once per
	//         crossover and never permanently hijacks theme shopping.
	//     (Hydrogen is the cart source of truth: most shoppers land there and the
	//     theme is a secondary surface that redirects back to Hydrogen.)
	//
	//   FORWARD  theme -> Hydrogen   cookie `np_cart_sync`
	//     We mirror the theme's current cart token here. Hydrogen reads it (via
	//     cart.getId in server.ts) and adopts it only when it has no cart of its own.
	//
	// WHEN IT RUNS
	//   On page load, and again whenever the shopper RETURNS to this tab
	//   (`visibilitychange` -> visible, plus `pageshow` from the back/forward cache).
	//   The return-to-tab pass also repaints the cart in place, so a cart changed in
	//   another tab or on the other storefront shows up without a navigation. It's
	//   throttled (5s) so rapid tab-switching stays cheap.
	//
	// SAFETY — why this cannot break either cart
	//   • FORWARD never touches Shopify's native `cart` cookie; it only writes the
	//     separate `np_cart_sync` cookie.
	//   • REVERSE writes the native `cart` cookie HOST-ONLY (no Domain), exactly
	//     like Shopify does, so it never creates a second/ambiguous `cart` cookie.
	//     It only acts when the Hydrogen token DIFFERS from the current theme token.
	//   • Two separate shared cookies are used so the two directions never overwrite
	//     each other.
	//   • The cart token is STABLE for the cart's life, so mirroring it once per
	//     page load is enough; live line items live server-side under that token.
	//   • Only widens cookie domains on *.nativepath.com hosts. On myshopify.com /
	//     dev / localhost the cross-domain handoff is simply inactive.
	//
	// KEEP ALIGNED WITH
	//   nativepath-v2/server.ts    (Hydrogen side: reads np_cart_sync, writes np_cart_hydrogen)
	//   assets/landing-params.js   (same cross-subdomain cookie pattern)
	//   docs/cart-sync.md          (end-to-end explanation)

	var SYNC_COOKIE = 'np_cart_sync'; // FORWARD: theme -> Hydrogen
	var HYDROGEN_COOKIE = 'np_cart_hydrogen'; // REVERSE: Hydrogen -> theme
	var NATIVE_CART_COOKIE = 'cart'; // Shopify's native cart cookie

	// 14 days, matching Shopify's own native `cart` cookie (per shopify.com/legal/
	// cookies). We rewrite np_cart_sync on every page load so this window rolls
	// forward with activity, exactly like the native cart cookie. We deliberately
	// do NOT use the 30-day cart-object lifetime: np_cart_sync should die when the
	// theme's own cart cookie does, so it never points Hydrogen at a cart the theme
	// itself has already forgotten.
	var SYNC_MAX_AGE = 60 * 60 * 24 * 14;

	function readCookie(name) {
		// `(?:^|; )` = start of the cookie string or right after "; ".
		var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
		return m ? decodeURIComponent(m[1]) : '';
	}

	function isNativePathHost() {
		// Matches `nativepath.com` and any subdomain (e.g. shop.nativepath.com),
		// but NOT `nativepath-dev2.myshopify.com`.
		return /(^|\.)nativepath\.com$/.test(location.hostname);
	}

	// Attributes for the SHARED cross-subdomain cookies (np_cart_sync / np_cart_hydrogen).
	function sharedAttrs() {
		var attrs = ['Path=/', 'SameSite=Lax'];
		if (isNativePathHost()) attrs.unshift('Domain=.nativepath.com');
		if (location.protocol === 'https:') attrs.push('Secure');
		return attrs;
	}

	function writeSharedCookie(name, value) {
		document.cookie =
			[name + '=' + encodeURIComponent(value)]
				.concat(sharedAttrs())
				.concat(['Max-Age=' + SYNC_MAX_AGE])
				.join('; ') + ';';
	}

	function clearSharedCookie(name) {
		document.cookie = [name + '=', 'Max-Age=0'].concat(sharedAttrs()).join('; ') + ';';
	}

	// Set the HOST-ONLY native `cart` cookie (mirroring how Shopify sets it).
	function setNativeCartCookie(token) {
		var parts = [NATIVE_CART_COOKIE + '=' + encodeURIComponent(token), 'Path=/', 'SameSite=Lax'];
		if (location.protocol === 'https:') parts.push('Secure');
		document.cookie = parts.join('; ') + ';';
	}

	// Fetch the CURRENT cart (whatever the `cart` cookie points at right now).
	function fetchCart() {
		return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
			.then(function (r) { return r.ok ? r.json() : null; });
	}

	// -------------------------------------------------------------------------
	// REVERSE: adopt a cart handed over from Hydrogen. Returns a promise that
	// resolves once the native cart cookie has been settled, so FORWARD mirrors
	// the final token (not a token we're about to revert).
	// -------------------------------------------------------------------------
	function adoptHydrogenCart() {
		var hydrogenToken = readCookie(HYDROGEN_COOKIE);
		if (!hydrogenToken) return Promise.resolve(); // nothing handed over from Hydrogen

		var nativeToken = readCookie(NATIVE_CART_COOKIE);
		if (hydrogenToken === nativeToken) {
			// Already in sync — just consume the cookie so it can't re-adopt after
			// the shopper later builds a different theme cart.
			clearSharedCookie(HYDROGEN_COOKIE);
			return Promise.resolve();
		}

		// "Hydrogen wins" — BUT only when Hydrogen actually has items. An EMPTY
		// Hydrogen cart must not wipe a cart the shopper has on the theme. We can't
		// read another cart's contents without making it current, so tentatively
		// switch to the Hydrogen cart, then confirm it's non-empty; if it's empty,
		// restore the theme cart. Consume the shared cookie either way.
		setNativeCartCookie(hydrogenToken);
		clearSharedCookie(HYDROGEN_COOKIE);

		return fetchCart()
			.then(function (cart) {
				if (cart && cart.item_count > 0) {
					// Hydrogen has items → keep it. Patch the current page's visible
					// cart surfaces in place (no reload) via the Section Rendering API,
					// which renders in the context of the cart cookie we just set.
					refreshCartSections(cart);
				} else {
					// Hydrogen cart is empty → keep the theme's original cart. The page
					// was already rendered with it, so no repaint is needed.
					setNativeCartCookie(nativeToken);
				}
			})
			.catch(function () {
				// On error, don't risk stranding the shopper on a possibly-empty
				// adopted cart — restore the theme cart.
				setNativeCartCookie(nativeToken);
			});
	}

	// Parse a rendered section's HTML and return the innerHTML of `selector`.
	function sectionInner(html, selector) {
		var el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
		return el ? el.innerHTML : null;
	}

	// Re-render the theme's cart surfaces present on this page, in place. Reuses the
	// same section names/selectors the theme itself uses (assets/cart.js,
	// cart-drawer.js) so we stay consistent with how the theme refreshes its cart.
	//
	// Uses BUNDLED section rendering (`sections` + `sections_url` posted to the cart
	// AJAX endpoint) rather than the standalone `?sections=` GET. That matters: the
	// standalone GET is resolved against the page's own template, so on a product URL
	// `/products/x?sections=cart-icon-bubble` returns the `product` section instead —
	// the requested cart sections simply never come back, and the refresh silently did
	// nothing on every page except the homepage. The bundled form returns the cart AND
	// the requested sections correctly on any path (verified on a live product page),
	// and it's what Dawn's own cart.js/cart-drawer.js use.
	//
	// `updates: {}` makes it a no-op mutation: it does not change the cart, it just
	// re-renders it (in the context of the `cart` cookie we may have just swapped).
	// It also returns the cart itself, so `knownCart` is only used to avoid an extra
	// /cart.js round-trip for the empty-check the caller already did.
	function refreshCartSections(knownCart) {
		var jobs = [];

		// Header cart count — present on virtually every page.
		var bubble = document.getElementById('cart-icon-bubble');
		if (bubble) {
			jobs.push({ name: 'cart-icon-bubble', parse: '.shopify-section', apply: function (inner) { bubble.innerHTML = inner; } });
		}
		// Cart drawer, if this theme renders one.
		var drawer = document.getElementById('CartDrawer');
		if (drawer) {
			jobs.push({ name: 'cart-drawer', parse: '#CartDrawer', apply: function (inner) { drawer.innerHTML = inner; } });
		}
		// Cart page main sections (their section name is the template-specific id on
		// the element's dataset — same lookup the theme's cart.js uses).
		[['main-cart-items', '.js-contents'], ['main-cart-footer', '.js-contents']].forEach(function (pair) {
			var el = document.getElementById(pair[0]);
			if (el && el.dataset && el.dataset.id) {
				jobs.push({ name: el.dataset.id, parse: pair[1], apply: function (inner) { (el.querySelector(pair[1]) || el).innerHTML = inner; } });
			}
		});

		if (!jobs.length) return; // nothing visible to patch on this page

		var names = jobs.map(function (j) { return j.name; });
		fetch('/cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
			credentials: 'same-origin',
			// `updates: {}` = re-render only, no cart change. `sections_url` is the page
			// the sections should be rendered for.
			body: JSON.stringify({ updates: {}, sections: names, sections_url: location.pathname }),
		})
			.then(function (r) { return r.ok ? r.json() : null; })
			.then(function (res) {
				// The bundled response IS the cart, and carries the rendered sections.
				var cart = res || knownCart;
				var sections = res && res.sections;
				if (sections) {
					jobs.forEach(function (j) {
						var html = sections[j.name];
						if (!html) return;
						var inner = sectionInner(html, j.parse);
						if (inner != null) { try { j.apply(inner); } catch (e) { /* skip this surface */ } }
					});
				}
				if (cart) {
					// Match the theme: toggle the empty-state classes on the cart wrappers.
					var isEmpty = cart.item_count === 0;
					['cart-drawer', 'cart-items', 'cart-drawer-items'].forEach(function (tag) {
						var node = document.querySelector(tag);
						if (node) node.classList.toggle('is-empty', isEmpty);
					});
					var footer = document.getElementById('main-cart-footer');
					if (footer) footer.classList.toggle('is-empty', isEmpty);
				}
			})
			.catch(function () {
				// No reload. The cookie is set, so the next render is correct anyway.
			});
	}

	// -------------------------------------------------------------------------
	// FORWARD: mirror the theme's current cart token out to Hydrogen.
	// -------------------------------------------------------------------------
	function mirrorCartToHydrogen() {
		// Primary source: Shopify's native `cart` cookie (`<token>?key=<secret>`),
		// set as soon as a cart exists.
		var token = readCookie(NATIVE_CART_COOKIE);
		if (token) {
			// Write on every load (not only on change) so the 14-day Max-Age rolls
			// forward with activity, mirroring Shopify's native cart cookie.
			writeSharedCookie(SYNC_COOKIE, token);
			return;
		}
		// Fallback (native cookie absent): /cart.js also returns the token with key.
		fetchCart()
			.then(function (cart) {
				if (cart && cart.token) writeSharedCookie(SYNC_COOKIE, cart.token);
				else clearSharedCookie(SYNC_COOKIE);
			})
			.catch(function () {
				// Stay silent — a sync failure must never block the page.
			});
	}

	// One sync pass: REVERSE (adopt a handed-off Hydrogen cart) then FORWARD (mirror
	// the resulting token out). adoptHydrogenCart() may revert the cart cookie (if
	// Hydrogen's cart is empty), so we wait for it to settle before FORWARD mirrors
	// the FINAL token — otherwise we'd mirror a token we're about to discard.
	// `repaint` re-renders the current cart's contents in place; we only do that on a
	// return-to-tab, not on the initial load (where the server already rendered the
	// correct cart).
	function sync(repaint) {
		return adoptHydrogenCart()
			.catch(function () {})
			.then(function () {
				mirrorCartToHydrogen();
				if (repaint) refreshCartSections();
			})
			.catch(function () {
				// Swallow anything thrown in the forward/repaint step too, so a sync
				// pass can never surface an unhandled rejection.
			});
	}

	function run() {
		try {
			sync(false); // initial load

			// Re-sync when the shopper returns to this tab, so a cart changed in
			// another tab or on the other storefront shows up without a navigation.
			// `visibilitychange` covers tab switches; `pageshow` with `persisted`
			// covers back/forward-cache restores (where `load` doesn't fire again).
			// Throttled so rapid tab-flicking stays cheap.
			var THROTTLE_MS = 5000;
			var lastSyncAt = Date.now();
			function onReturn() {
				// Runs on a browser event (outside run()'s try/catch), so guard it.
				try {
					if (document.visibilityState !== 'visible') return;
					var now = Date.now();
					if (now - lastSyncAt < THROTTLE_MS) return;
					lastSyncAt = now;
					sync(true);
				} catch (e) {
					// Never let a focus handler throw.
				}
			}
			document.addEventListener('visibilitychange', onReturn);
			window.addEventListener('pageshow', function (e) { if (e && e.persisted) onReturn(); });
		} catch (e) {
			// Never throw from a best-effort sync helper.
		}
	}

	run();
})();
